// src/components/NewTournamentDialog.jsx
// Shown before a tournament is created, so the choices that shape it are made on purpose, not discovered
// later: the format especially, since it locks in once a round (or, for fixed teams, a team) exists.
// Ranking and matchmaking are here too, but only the method itself (no sliders) — those stay changeable
// at any time in Settings, so getting them exactly right now does not matter.
import React, { useEffect, useState } from 'react';
import { apiRequest } from '../api';

const MAX_COURTS = 50; // keep in sync with Tournament.MAX_COURTS in backend/entities.py
const MAX_TEAMS_PER_GROUP = 32; // keep in sync with Tournament.MAX_TEAMS_PER_GROUP in backend/entities.py
const MAX_ADVANCE = 8; // keep in sync with Tournament.MAX_ADVANCE

function NewTournamentDialog({ userId, onCreate, onClose }) {
    const [catalog, setCatalog] = useState(null); // null: still loading
    const [format, setFormat] = useState('rotating');
    const [courts, setCourts] = useState('');
    const [singles, setSingles] = useState(false);
    const [teamsPerGroup, setTeamsPerGroup] = useState('4');
    const [advancePerGroup, setAdvancePerGroup] = useState(2);
    const [thirdPlace, setThirdPlace] = useState(true);
    const [rankingMethod, setRankingMethod] = useState('');
    const [matchmakingMethod, setMatchmakingMethod] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let ignore = false;
        apiRequest('GET', '/algorithms', userId)
            .then((loaded) => {
                if (ignore) return;
                setCatalog(loaded);
                setRankingMethod(loaded.defaults.ranking);
                setMatchmakingMethod(loaded.defaults.matchmaking);
            })
            .catch(() => {}); // the selects just stay empty; a bare tournament still gets sensible server defaults
        return () => {
            ignore = true;
        };
    }, [userId]);

    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    const wholeBetween = (value, min, max) => Number.isInteger(Number(value)) && value !== '' && Number(value) >= min && Number(value) <= max;
    const groupsInvalid = format === 'groups-finals' && !wholeBetween(teamsPerGroup, 2, MAX_TEAMS_PER_GROUP);
    const courtsInvalid = courts !== '' && !(Number.isInteger(Number(courts)) && Number(courts) >= 1 && Number(courts) <= MAX_COURTS);

    const create = async (event) => {
        event.preventDefault();
        if (courtsInvalid || groupsInvalid) return;
        setBusy(true);
        await onCreate({
            format,
            ...(format === 'groups-finals' ? { teamsPerGroup: Number(teamsPerGroup), advancePerGroup, thirdPlace } : {}),
            courts: courts === '' ? null : Number(courts),
            singlesEnabled: format === 'rotating' ? singles : false,
            rankingMethod: rankingMethod || undefined,
            matchmakingMethod: matchmakingMethod || undefined,
        });
        setBusy(false);
    };

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <form
                className="modal new-tournament-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="new-tournament-title"
                onClick={(event) => event.stopPropagation()}
                onSubmit={create}
            >
                <h2 id="new-tournament-title">New tournament</h2>
                <p className="hint">
                    A few choices to start with. Most of this can be changed later in Settings — the format is the one
                    that cannot, once a round (or a team) exists.
                </p>

                <fieldset className="format-kind" role="radiogroup" aria-label="Format">
                    {[
                        ['rotating', 'Rotating players', 'Partners and opponents change every round.'],
                        ['fixed-teams', 'Fixed teams', 'Pairs stay the same for the whole tournament.'],
                        ['groups-finals', 'Groups & finals', 'Teams play in groups, then the best go through to a knockout.'],
                    ].map(([key, label, hint]) => (
                        <label key={key} className={`format-option${format === key ? ' chosen' : ''}`}>
                            <input type="radio" name="new-format" checked={format === key} onChange={() => setFormat(key)} />
                            <span>
                                <strong>{label}</strong>
                                <span className="hint">{hint}</span>
                            </span>
                        </label>
                    ))}
                </fieldset>

                <div className="details-grid">
                    <label className="field">
                        <span>Courts</span>
                        <input
                            className="input"
                            type="number"
                            min="1"
                            max={MAX_COURTS}
                            inputMode="numeric"
                            placeholder="No limit"
                            value={courts}
                            onChange={(event) => setCourts(event.target.value)}
                        />
                    </label>
                    {catalog && (
                        <label className="field">
                            <span>Ranking</span>
                            <select className="input" value={rankingMethod} onChange={(event) => setRankingMethod(event.target.value)}>
                                {catalog.ranking.map((method) => (
                                    <option key={method.id} value={method.id}>
                                        {method.name}
                                    </option>
                                ))}
                            </select>
                        </label>
                    )}
                    {catalog && (
                        <label className="field">
                            <span>Matchmaking</span>
                            <select className="input" value={matchmakingMethod} onChange={(event) => setMatchmakingMethod(event.target.value)}>
                                {catalog.matchmaking.map((method) => (
                                    <option key={method.id} value={method.id}>
                                        {method.name}
                                    </option>
                                ))}
                            </select>
                        </label>
                    )}
                </div>
                {courtsInvalid && (
                    <p className="form-error" role="alert">
                        Courts must be a whole number from 1 to {MAX_COURTS}.
                    </p>
                )}

                {format === 'groups-finals' && (
                    <>
                        <div className="details-grid">
                            <label className="field">
                                <span>Teams per group</span>
                                <input
                                    className="input"
                                    type="number"
                                    min="2"
                                    max={MAX_TEAMS_PER_GROUP}
                                    inputMode="numeric"
                                    value={teamsPerGroup}
                                    onChange={(event) => setTeamsPerGroup(event.target.value)}
                                />
                            </label>
                            <label className="field">
                                <span>Advancing per group</span>
                                <select className="input" value={advancePerGroup} onChange={(event) => setAdvancePerGroup(Number(event.target.value))}>
                                    {Array.from({ length: MAX_ADVANCE }, (_, i) => i + 1).map((n) => (
                                        <option key={n} value={n}>
                                            {n}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        </div>
                        {groupsInvalid && (
                            <p className="form-error" role="alert">
                                Teams per group must be a whole number from 2 to {MAX_TEAMS_PER_GROUP}.
                            </p>
                        )}
                        <p className="hint">The number of groups follows from how many teams you make.</p>
                        <label className="check-row">
                            <input type="checkbox" checked={thirdPlace} onChange={(event) => setThirdPlace(event.target.checked)} /> Play a match for third place
                        </label>
                    </>
                )}

                {format === 'rotating' && (
                    <div className="singles-toggle">
                        <button
                            type="button"
                            role="switch"
                            aria-checked={singles}
                            aria-labelledby="new-singles-label"
                            className={`switch${singles ? ' on' : ''}`}
                            onClick={() => setSingles((prev) => !prev)}
                        >
                            <span className="switch-knob" />
                        </button>
                        <strong id="new-singles-label">Singles for players sitting out</strong>
                    </div>
                )}

                <div className="modal-actions">
                    <button type="button" className="btn" onClick={onClose} disabled={busy}>
                        Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={busy || courtsInvalid || groupsInvalid}>
                        Create tournament
                    </button>
                </div>
            </form>
        </div>
    );
}

export default NewTournamentDialog;
